/* eslint-disable no-console, import/prefer-default-export */
import { announceMessage } from '../accessibility/status-messages';
import {
  dispatchSiteEvent,
  siteEventNames,
} from '../../events/site-events';
import { login } from './account-api';
import { openAccountModal, closeAccountModal } from './account-modal';
import { addCreateAccountCallbacks, resetCreateAccountForm, setCreateAccountFocus } from './create-account';
import { runAfterAuth } from './post-auth';
import { validate } from '../../utils/form-validation-utils';

let initialLanuch = true;

// Latest callbacks from launchLoginModal. Listeners are registered once (initialLanuch) but must
// always invoke the callbacks from the most recent open — otherwise the first caller (e.g. header
// login → My Account redirect) permanently steals success/fail from later callers (swatch heart/cart).
let activeCallbacks = null;

const loginModalId = 'acct-login-modal';

const resolveCallbacks = () => activeCallbacks || window.onSubmitCallbacks || null;

const onLoginSubmit = (event, callbacks) => {
  event.preventDefault();
  const modalForm = document.querySelector('.acct-login-modal-form');
  const modalErrorMsg = modalForm.querySelector('.form-error-message');
  if (validate(modalForm)) {
    modalErrorMsg.classList.add('hide');
    let remember = false;
    if (document.querySelectorAll('#modalAcctRememberMe:checked').length === 1) {
      remember = true;
    }
    // Resolve the reCAPTCHA challenge token to send. When enforcement is off (e.g. Dev) we never
    // gate on it. When on and a low score has shown an interactive challenge, it must be solved
    // first; otherwise (high score / no challenge) we send the "no-challenge-needed" sentinel.
    let recaptchaChallenge = 'no-challenge-needed';
    if (window.loginRecaptchaEnabled !== false) {
      const challengeWrapper = modalForm.querySelector('.grecaptcha-challenge-wrapper');
      const challengeIpt = modalForm.querySelector('.recaptcha-challenge-input');
      const challengeVisible = challengeWrapper && !challengeWrapper.classList.contains('hidden');
      if (challengeVisible && (!challengeIpt || challengeIpt.value === '')) {
        const errorEl = challengeWrapper.querySelector('[id^="recaptchaError"]');
        if (errorEl) {
          errorEl.classList.remove('field-validation-valid');
          errorEl.classList.add('field-validation-error');
          announceMessage(errorEl.innerHTML);
        }
        return;
      }
      if (challengeIpt && challengeIpt.value) {
        recaptchaChallenge = challengeIpt.value;
      }
    }
    login(
      {
        username: document.getElementById('modalAcctUsername').value,
        password: document.getElementById('modalAcctPassword').value,
        rememberMe: remember,
        recaptchaChallenge,
      },
      {
        onSuccess: (data) => {
          if (!data.success) {
            modalErrorMsg.classList.remove('hide');
            announceMessage(modalErrorMsg.innerHTML);
            // The reCAPTCHA token was consumed by this attempt; reset the widget so the user can
            // re-solve and retry (the full-page form gets this for free via its post-back reload).
            if (window.grecaptchaApi && typeof window.grecaptchaApi.resetChallenge === 'function') {
              window.grecaptchaApi.resetChallenge(modalForm);
            }
          } else {
            window.isAuthenticated = true;
            window.serverIsAuthenticated = true;
            window.userId = data['user-id'];
            dispatchSiteEvent(siteEventNames.ON_LOGGED_IN);
            runAfterAuth(() => {
              const cbs = callbacks || resolveCallbacks();
              if (cbs && cbs.onLoginSuccess) {
                cbs.onLoginSuccess();
              }
            });
            const modal = document.querySelector(`#${loginModalId}`);
            closeAccountModal(modal);
          }
        },
        onError: () => {
          const cbs = callbacks || resolveCallbacks();
          if (!cbs) {
            return;
          }
          if (cbs.onLoginFail) {
            cbs.onLoginFail();
          }
        },
      },
    );
  }
};

const onModalClose = (event, callback) => {
  if (callback) {
    callback();
  }
};

const toggleCreateAccountForm = (isVisible) => {
  const modal = document.getElementById(loginModalId);
  if (isVisible) {
    modal.querySelector('.account-login-container').classList.add('hidden');
    modal.querySelector('.login-side-column').classList.add('hidden');
    modal.querySelector('.create-account-container').classList.remove('hidden');
    modal.querySelector('.create-account-side-column').classList.remove('hidden');
    if (modal.querySelectorAll('.login-message-wrapper').length !== 0) {
      modal.querySelector('.login-message-wrapper').classList.add('hidden');
    }
    modal.style.height = '600px';
  } else {
    modal.querySelector('.account-login-container').classList.remove('hidden');
    modal.querySelector('.login-side-column').classList.remove('hidden');
    modal.querySelector('.create-account-container').classList.add('hidden');
    modal.querySelector('.create-account-side-column').classList.add('hidden');
    modal.removeAttribute('style');
    if (modal.querySelectorAll('.login-message-wrapper').length !== 0) {
      modal.querySelector('.login-message-wrapper').classList.remove('hidden');
    }
  }
};

const launchLoginModal = (options) => {
  // Reset modal
  toggleCreateAccountForm();
  resetCreateAccountForm({
    form: document.querySelector('.modal-acct-create-form'),
  });
  const {
    type,
    callbacks,
  } = options;
  activeCallbacks = callbacks || null;
  // create-account.js reads window.onSubmitCallbacks; keep it aligned with this launch.
  window.onSubmitCallbacks = activeCallbacks;
  const modal = document.getElementById(loginModalId);
  if (type) {
    modal.classList.add('c-modal--loginCTA');
    modal.querySelector('.login-message-wrapper').classList.remove('hidden');
    let msg = '';
    switch (type) {
      case 'photo':
        msg = window.loginMessagePhoto;
        break;
      case 'location':
        msg = window.loginMessageDealer;
        break;
      case 'visualization':
        msg = window.loginMessageVisualization;
        break;
      default:
        break;
    }
    modal.querySelector('.login-message-wrapper p').innerHTML = msg;
  } else {
    modal.classList.remove('c-modal--loginCTA');
    modal.querySelector('.login-message-wrapper').classList.add('hidden');
    modal.querySelector('.login-message-wrapper p').innerHTML = '';
  }
  const overlay = document.querySelector('#modal-overlay');
  openAccountModal(modal);
  // Submit modal form
  const loginSubmitBtn = modal.querySelector('.acct-login-modal-form .o-form__submit');
  const modalCloseBtn = modal.querySelector('#close-btn');
  // Only run once — handlers resolve callbacks at click time via activeCallbacks.
  if (initialLanuch) {
    initialLanuch = false;
    loginSubmitBtn.addEventListener('click', (event) => {
      onLoginSubmit(event, resolveCallbacks());
    });
    modalCloseBtn.addEventListener('click', (event) => {
      event.stopPropagation();
      const cbs = resolveCallbacks();
      onModalClose(event, cbs && cbs.onLoginFail);
    });
    if (overlay) {
      overlay.addEventListener('click', (event) => {
        const cbs = resolveCallbacks();
        onModalClose(event, cbs && cbs.onLoginFail);
      });
    }
    // Toggle create account form (wired on first open when that open is a CTA type).
    // Later CTA opens still work because the listener resolves callbacks at click time.
    if (type === 'visualization' || type === 'photo' || type === 'location') {
      [].map.call(document.querySelectorAll('.create-account-btn, .acct-login-modal-form .form-error-message a'), btn => btn.addEventListener('click', (event) => {
        const cbs = resolveCallbacks();
        setCreateAccountFocus({
          form: document.querySelector('.modal-acct-create-form'),
        });
        event.preventDefault();
        addCreateAccountCallbacks({
          onLoginSuccess: () => {
            // Get opt-in status and dispatch event
            const optinIpt = document.getElementById('modal-offer-opt-in');
            const optin = optinIpt ? optinIpt.checked : false;
            dispatchSiteEvent(siteEventNames.ON_ACCOUNT_CREATED, { optin });
            if (cbs && cbs.onLoginSuccess) {
              cbs.onLoginSuccess(true);
            }
            closeAccountModal(modal);
          },
          onLoginFail: () => {
            if (cbs && cbs.onLoginFail) {
              cbs.onLoginFail();
            }
          },
        });
        toggleCreateAccountForm(true);
      }));
    }
  }
};

export {
  launchLoginModal,
};
/* eslint-enable no-console, import/prefer-default-export */
